import React, { useState, useEffect, useCallback } from 'react';
import api from '../../services/api';

/**
 * BatchTestTab — trigger AutoTestRunner batch executions from the UI.
 *
 * Flow:
 *   1. User picks preset OR scenarios (checkbox multi-select)
 *   2. Chooses Mock or Live mode
 *   3. Confirmation modal on Live
 *   4. POST /voice-test/runs → server spawns detached child process
 *   5. UI polls GET /voice-test/runs/:id every 3s for progress
 *   6. Live progress panel + cancel button
 */
function BatchTestTab({ practiceId }) {
  const [scenarios, setScenarios] = useState([]);
  const [presets, setPresets] = useState({});
  const [selectedPreset, setSelectedPreset] = useState('p0');
  const [selectedScenarios, setSelectedScenarios] = useState([]);
  const [customPick, setCustomPick] = useState(false);
  const [mode, setMode] = useState('mock');
  const [parallelism, setParallelism] = useState(6);
  const [showLiveConfirm, setShowLiveConfirm] = useState(false);
  const [liveConfirmChecked, setLiveConfirmChecked] = useState(false);
  const [starting, setStarting] = useState(false);
  const [error, setError] = useState('');
  const [activeRun, setActiveRun] = useState(null);
  const [pollTick, setPollTick] = useState(0);

  useEffect(() => {
    if (!practiceId) return;
    api.request(`/practices/${practiceId}/voice-test/scenarios`)
      .then(res => {
        setScenarios(res?.scenarios || []);
        setPresets(res?.presets || {});
      })
      .catch(err => setError('Failed to load scenarios: ' + (err?.message || 'unknown')));
  }, [practiceId]);

  // Poll active run every 3s
  useEffect(() => {
    if (!activeRun) return;
    const interval = setInterval(() => setPollTick(t => t + 1), 3000);
    return () => clearInterval(interval);
  }, [activeRun]);

  useEffect(() => {
    if (!activeRun) return;
    api.request(`/practices/${practiceId}/voice-test/runs/${activeRun.id}`)
      .then(res => {
        if (!res?.run) return;
        setActiveRun(res.run);
        // Stop polling when run is terminal
        if (['completed', 'cancelled', 'force_killed', 'failed'].includes(res.run.status)) {
          setActiveRun(prev => ({ ...(prev || {}), ...res.run, scenarios: res.scenarios || [] }));
        }
      })
      .catch(() => {});
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pollTick]);

  const effectiveScenarios = customPick ? selectedScenarios : (presets[selectedPreset] || []);
  const costPerScenario = mode === 'live' ? 0.25 : 0.15;
  const estCost = (effectiveScenarios.length * costPerScenario).toFixed(2);
  const wallEstMin = Math.max(1, Math.ceil(effectiveScenarios.length * 90 / parallelism / 60));

  const startRun = useCallback(async () => {
    setStarting(true);
    setError('');
    try {
      const body = {
        mode,
        parallelism,
        ...(customPick ? { scenarios: selectedScenarios } : { preset: selectedPreset }),
      };
      const res = await api.request(`/practices/${practiceId}/voice-test/runs`, { method: 'POST', body });
      setActiveRun(res.run);
      setShowLiveConfirm(false);
      setLiveConfirmChecked(false);
    } catch (err) {
      setError(err.message);
    } finally {
      setStarting(false);
    }
  }, [practiceId, mode, parallelism, customPick, selectedScenarios, selectedPreset]);

  const cancelRun = useCallback(async () => {
    if (!activeRun) return;
    try {
      await api.request(`/practices/${practiceId}/voice-test/runs/${activeRun.id}/cancel`, { method: 'POST' });
    } catch (err) {
      setError('Cancel failed: ' + err.message);
    }
  }, [activeRun, practiceId]);

  const handleRunClick = () => {
    if (mode === 'live') setShowLiveConfirm(true);
    else startRun();
  };

  if (activeRun && !['completed', 'cancelled', 'force_killed', 'failed'].includes(activeRun.status)) {
    return (
      <div style={S.card}>
        <h3 style={S.h3}>Run in progress — id {activeRun.id}</h3>
        <div style={S.progressBox}>
          <div><b>Status:</b> {activeRun.status}</div>
          <div><b>Practice:</b> {activeRun.practice_id}</div>
          <div><b>Mode:</b> {activeRun.mode}</div>
          <div><b>Progress:</b> {activeRun.finished_scenarios || 0} / {activeRun.total_scenarios || 0}</div>
          <div><b>Verdicts:</b> PASS={activeRun.pass_count || 0}  PARTIAL={activeRun.partial_count || 0}  FAIL={activeRun.fail_count || 0}</div>
          <div><b>Est cost:</b> ${Number(activeRun.cost_est_usd || 0).toFixed(2)}</div>
        </div>
        <button style={S.btnDanger} onClick={cancelRun}>Cancel Run</button>
      </div>
    );
  }

  return (
    <div style={S.container}>
      {error && <div style={S.err}>{error}</div>}

      {activeRun && (
        <div style={S.completedBanner}>
          Last run <b>#{activeRun.id}</b>: {activeRun.status} · PASS={activeRun.pass_count || 0} PARTIAL={activeRun.partial_count || 0} FAIL={activeRun.fail_count || 0}
          {activeRun.mode === 'live' && (
            <span> · cleanup={activeRun.cleanup_status || 'pending'}{activeRun.cleanup_summary?.reverted !== undefined ? ` (reverted ${activeRun.cleanup_summary.reverted}, failed ${activeRun.cleanup_summary.failed}, manual ${activeRun.cleanup_summary.manual_review})` : ''}</span>
          )}
        </div>
      )}

      <div style={S.card}>
        <h3 style={S.h3}>Batch test</h3>

        <div style={S.row}>
          <label style={S.label}>Scenarios:</label>
          <div>
            <label style={S.radio}>
              <input type="radio" checked={!customPick} onChange={() => setCustomPick(false)} />
              Preset:
              <select style={S.select} value={selectedPreset} onChange={e => setSelectedPreset(e.target.value)} disabled={customPick}>
                {Object.keys(presets).map(p => (
                  <option key={p} value={p}>{p} ({(presets[p] || []).length} scenarios)</option>
                ))}
              </select>
            </label>
            <label style={S.radio}>
              <input type="radio" checked={customPick} onChange={() => setCustomPick(true)} />
              Custom pick
            </label>
          </div>
        </div>

        {customPick && (
          <div style={S.scenarioList}>
            {scenarios.map(s => (
              <label key={s.key} style={S.scenarioRow}>
                <input
                  type="checkbox"
                  checked={selectedScenarios.includes(s.key)}
                  onChange={e => {
                    setSelectedScenarios(prev => e.target.checked
                      ? [...prev, s.key]
                      : prev.filter(k => k !== s.key));
                  }}
                />
                <span style={{ marginLeft: 6 }}>
                  <b>{s.key}</b> — {s.description}
                  {s.tags && s.tags.length > 0 && (
                    <span style={S.tags}>{s.tags.map(t => `#${t}`).join(' ')}</span>
                  )}
                </span>
              </label>
            ))}
          </div>
        )}

        <div style={S.row}>
          <label style={S.label}>Mode:</label>
          <label style={S.radio}>
            <input type="radio" checked={mode === 'mock'} onChange={() => setMode('mock')} />
            Mock (safe — no Athena writes)
          </label>
          <label style={S.radio}>
            <input type="radio" checked={mode === 'live'} onChange={() => setMode('live')} />
            Live (real Athena — QA only, auto-cleanup)
          </label>
        </div>

        <div style={S.row}>
          <label style={S.label}>Parallelism:</label>
          <input type="number" min="1" max="8" value={parallelism} onChange={e => setParallelism(parseInt(e.target.value, 10) || 6)} style={{ width: 60 }} />
          <span style={S.hint}>1-8 (L7 Bedrock TPS cap)</span>
        </div>

        <div style={S.summary}>
          <div><b>Scenarios:</b> {effectiveScenarios.length}</div>
          <div><b>Estimated cost:</b> ${estCost}</div>
          <div><b>Estimated wall time:</b> ~{wallEstMin} min</div>
        </div>

        <button
          style={mode === 'live' ? S.btnDanger : S.btnPrimary}
          disabled={starting || effectiveScenarios.length === 0}
          onClick={handleRunClick}
        >
          {starting ? 'Starting...' : `Run Batch${mode === 'live' ? ' (LIVE)' : ''}`}
        </button>
      </div>

      {showLiveConfirm && (
        <div style={S.modalOverlay} onClick={() => setShowLiveConfirm(false)}>
          <div style={S.modal} onClick={e => e.stopPropagation()}>
            <h3 style={S.h3}>⚠ Live tool mode</h3>
            <p>This batch will make <b>REAL Athena calls</b> including:</p>
            <ul>
              <li>Up to {effectiveScenarios.length} bookings (auto-cancelled after)</li>
              <li>Any patient info updates (auto-reverted after)</li>
            </ul>
            <p><b>Practice:</b> {practiceId} · <b>Cleanup:</b> automatic after batch<br />
            <b>Fallback:</b> 24h sweep if per-run cleanup misses anything</p>
            <p><b>Estimated cost:</b> ${estCost}<br />
            <b>Estimated wall time:</b> ~{wallEstMin} min (batch) + ~5 min (cleanup)</p>
            <label style={{ display: 'block', margin: '16px 0' }}>
              <input type="checkbox" checked={liveConfirmChecked} onChange={e => setLiveConfirmChecked(e.target.checked)} />
              &nbsp;I understand this modifies QA sandbox state
            </label>
            <div style={{ textAlign: 'right' }}>
              <button style={S.btnSecondary} onClick={() => setShowLiveConfirm(false)}>Cancel</button>
              <button style={S.btnDanger} disabled={!liveConfirmChecked || starting} onClick={startRun}>
                {starting ? 'Starting...' : 'Run'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const S = {
  container: { display: 'flex', flexDirection: 'column', gap: 16 },
  card: { background: 'white', padding: 20, borderRadius: 8, border: '1px solid #e5e7eb' },
  h3: { margin: '0 0 16px', color: '#111827', fontSize: 18 },
  row: { display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 },
  label: { minWidth: 100, fontWeight: 600, color: '#374151' },
  radio: { display: 'inline-flex', alignItems: 'center', gap: 4, marginRight: 16, fontSize: 14 },
  select: { marginLeft: 8, padding: '4px 8px', borderRadius: 4, border: '1px solid #d1d5db' },
  scenarioList: { maxHeight: 320, overflow: 'auto', border: '1px solid #e5e7eb', padding: 8, borderRadius: 4, marginBottom: 12, background: '#fafafa' },
  scenarioRow: { display: 'block', padding: '3px 0', fontSize: 13 },
  tags: { marginLeft: 8, color: '#6b7280', fontSize: 11 },
  hint: { color: '#6b7280', fontSize: 12 },
  summary: { background: '#f3f4f6', padding: 12, borderRadius: 4, marginBottom: 12, fontSize: 14 },
  btnPrimary: { background: '#2563eb', color: 'white', border: 'none', padding: '10px 20px', borderRadius: 6, cursor: 'pointer', fontSize: 15 },
  btnDanger: { background: '#dc2626', color: 'white', border: 'none', padding: '10px 20px', borderRadius: 6, cursor: 'pointer', fontSize: 15, marginLeft: 8 },
  btnSecondary: { background: '#e5e7eb', color: '#111827', border: 'none', padding: '10px 20px', borderRadius: 6, cursor: 'pointer', fontSize: 15, marginRight: 8 },
  progressBox: { background: '#f3f4f6', padding: 16, borderRadius: 4, marginBottom: 16, fontSize: 14, lineHeight: 1.8 },
  completedBanner: { background: '#dbeafe', color: '#1e40af', padding: 10, borderRadius: 4, fontSize: 14 },
  err: { background: '#fee2e2', color: '#991b1b', padding: 10, borderRadius: 4, fontSize: 14 },
  modalOverlay: { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 100 },
  modal: { background: 'white', padding: 24, borderRadius: 8, maxWidth: 500, width: '90%' },
};

export default BatchTestTab;
